import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import es from 'date-fns/locale/es';
import { format } from 'date-fns';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { Calendar, Clock, User, AlertCircle, ChevronDown } from 'lucide-react';
import {
  getCartAvailability,
  getAvailableDates,
  getAvailableEmployees,
} from '../../services/appointmentService';

registerLocale('es', es);

// Convert "14:00" → "2:00 PM"
const toAmPm = (time24) => {
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr, 10);
  const suffix = h >= 12 ? 'PM' : 'AM';
  if (h > 12) h -= 12;
  if (h === 0) h = 12;
  return `${h}:${mStr} ${suffix}`;
};

const formatDuration = (mins) => {
  if (!mins) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

const DateTimeSelector = ({
  services,
  selectedDate,
  selectedSlot,
  notes,
  onDateChange,
  onSlotChange,
  onNotesChange,
  onBack,
  onContinue,
  onPickArtist,
}) => {
  const dateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
  const isToday = dateStr === format(new Date(), 'yyyy-MM-dd');

  /* Carrito normalizado: cada línea viaja como {service_id, employee_id|null}.
     La colaboradora es POR SERVICIO; el backend cruza la secuencia en orden. */
  const cartServices = useMemo(
    () => services
      .filter((s) => s.serviceId)
      .map((s) => ({ serviceId: s.serviceId, employeeId: s.employeeId || null })),
    [services]
  );
  const cartKey = useMemo(
    () => cartServices.map((s) => `${s.serviceId}:${s.employeeId || ''}`).join(','),
    [cartServices]
  );

  /* ---------- Días del mes con cupo (available-dates) ---------- */
  // 'yyyy-MM' → Set('yyyy-MM-dd') | null (falló la consulta: no bloquear)
  const [monthMap, setMonthMap] = useState({});
  const [visibleMonth, setVisibleMonth] = useState(
    format(selectedDate || new Date(), 'yyyy-MM')
  );

  // Si cambia el carrito, la disponibilidad por día cambia: invalidar cache
  useEffect(() => { setMonthMap({}); }, [cartKey]);

  useEffect(() => {
    if (!cartServices.length) return undefined;
    if (monthMap[visibleMonth] !== undefined) return undefined;
    let alive = true;
    getAvailableDates({ month: visibleMonth, services: cartServices })
      .then((res) => {
        if (!alive) return;
        setMonthMap((m) => ({
          ...m,
          [visibleMonth]: new Set(res.data?.available_dates || []),
        }));
      })
      .catch(() => {
        if (!alive) return;
        setMonthMap((m) => ({ ...m, [visibleMonth]: null }));
      });
    return () => { alive = false; };
  }, [visibleMonth, cartKey, monthMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterDate = useCallback((d) => {
    const set = monthMap[format(d, 'yyyy-MM')];
    if (set === undefined || set === null) return true; // aún cargando o falló: no bloquear
    return set.has(format(d, 'yyyy-MM-dd'));
  }, [monthMap]);

  /* ---------- Horarios del día (availability, modo carrito) ---------- */
  const [apiSlots, setApiSlots] = useState([]);
  const [totalDuration, setTotalDuration] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(false);
  const [staffHint, setStaffHint] = useState(false);

  // Si el día no tiene slots y hay colaboradoras elegidas, available-employees
  // dice si alguna de ellas es la que no tiene cupo ese día.
  const checkStaffAvailability = useCallback(async (date, cart) => {
    try {
      const res = await getAvailableEmployees({
        date,
        serviceIds: cart.map((s) => s.serviceId),
      });
      const bySvc = res.data?.employees_by_service || {};
      const conflict = cart.some((s) => {
        if (!s.employeeId) return false;
        const ids = bySvc[s.serviceId] || bySvc[String(s.serviceId)] || [];
        return !ids.includes(s.employeeId);
      });
      if (conflict) setStaffHint(true);
    } catch (err) { /* pista opcional: silencio si falla */ }
  }, []);

  const loadSlots = useCallback(async () => {
    if (!dateStr || !cartServices.length) {
      setApiSlots([]);
      return;
    }
    setSlotsLoading(true);
    setSlotsError(false);
    setStaffHint(false);
    try {
      // Una sola llamada: el backend cruza svc0 → svc1 → … con sus colaboradoras
      const res = await getCartAvailability({ date: dateStr, services: cartServices });
      const slots = res.data?.slots ?? [];
      setTotalDuration(res.data?.total_duration_minutes ?? null);
      setApiSlots(slots.map((s) => String(s.start || s).slice(0, 5)));
      if (slots.length === 0 && cartServices.some((s) => s.employeeId)) {
        checkStaffAvailability(dateStr, cartServices);
      }
    } catch (err) {
      setApiSlots([]);
      setSlotsError(true);
    } finally {
      setSlotsLoading(false);
    }
  }, [dateStr, cartKey, checkStaffAvailability]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadSlots(); }, [loadSlots]);

  const availableSlots = useMemo(() => {
    if (!dateStr) return [];
    if (!isToday) return apiSlots;
    const now = format(new Date(), 'HH:mm');
    return apiSlots.filter(s => s > now);
  }, [dateStr, isToday, apiSlots]);

  return (
    <div>
      <h4 className="h5 fw-semibold mb-3">Seleccione fecha y hora</h4>

      {/* Services summary */}
      {services.length > 0 && (
        <div className="mb-3" style={{ borderBottom: '1px solid #FCE4EC', paddingBottom: 12 }}>
          <small className="fw-semibold text-muted text-uppercase d-block mb-2" style={{ fontSize: '0.65rem', letterSpacing: '0.04em' }}>
            Servicios seleccionados
          </small>
          {services.map((svc, idx) => (
            <div
              key={idx}
              className="d-flex align-items-center justify-content-between py-1"
              style={{ borderBottom: idx < services.length - 1 ? '1px solid #f5f5f5' : 'none' }}
            >
              <div className="d-flex align-items-center flex-wrap gap-2" style={{ minWidth: 0, flex: 1 }}>
                <span className="small fw-medium" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{svc.shortName || svc.label}</span>
                {onPickArtist ? (
                  <button
                    type="button"
                    onClick={() => onPickArtist(idx)}
                    className="btn btn-sm py-0 px-2 d-inline-flex align-items-center gap-1"
                    style={{
                      fontSize: '0.7rem',
                      border: '1px solid #F8BBD0',
                      color: svc.employeeName ? '#EF60A3' : '#999',
                      backgroundColor: '#FFF0F5',
                      borderRadius: 20,
                    }}
                  >
                    <User size={10} /> {svc.employeeName || 'Elegir artista'} <ChevronDown size={10} />
                  </button>
                ) : svc.employeeName && (
                  <span
                    className="d-inline-flex align-items-center gap-1"
                    style={{ fontSize: '0.7rem', color: '#EF60A3' }}
                  >
                    <User size={10} /> {svc.employeeName}
                  </span>
                )}
              </div>
              {svc.price && (
                <span className="small fw-semibold flex-shrink-0 ms-2" style={{ color: '#EF60A3' }}>
                  {svc.price}
                </span>
              )}
            </div>
          ))}
          {totalDuration && (
            <div className="d-flex align-items-center gap-1 pt-2" style={{ fontSize: '0.75rem', color: '#C48BA0' }}>
              <Clock size={12} /> Duración total estimada: {formatDuration(totalDuration)}
            </div>
          )}
        </div>
      )}

      <div className="row g-4">
        {/* Date picker */}
        <div className="col-12 col-md-6">
          <label className="d-flex align-items-center gap-2 small fw-medium mb-2">
            <Calendar size={16} style={{ color: '#EF60A3' }} />
            Fecha
          </label>
          <DatePicker
            selected={selectedDate}
            onChange={(date) => { onDateChange(date); onSlotChange(''); }}
            dateFormat="MMMM d, yyyy"
            className="form-control"
            locale="es"
            minDate={new Date()}
            filterDate={filterDate}
            onMonthChange={(d) => setVisibleMonth(format(d, 'yyyy-MM'))}
            inline
          />
        </div>

        {/* Time slots */}
        <div className="col-12 col-md-6">
          <label className="d-flex align-items-center gap-2 small fw-medium mb-2">
            <Clock size={16} style={{ color: '#EF60A3' }} />
            Horarios disponibles
          </label>

          {slotsLoading ? (
            <div className="small text-muted d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" style={{ color: '#EF60A3' }} />
              Consultando horarios disponibles…
            </div>
          ) : slotsError ? (
            <div>
              <p className="small text-muted mb-2">No pudimos cargar los horarios. Revisa tu conexión.</p>
              <Button variant="outline-secondary" size="sm" onClick={loadSlots}>Intentar de nuevo</Button>
            </div>
          ) : availableSlots.length > 0 ? (
            <div className="d-flex flex-wrap gap-2">
              {availableSlots.map((time) => {
                const isSelected = selectedSlot === time;
                return (
                  <button
                    key={time}
                    onClick={() => onSlotChange(time)}
                    className="btn btn-sm"
                    style={{
                      fontSize: '0.78rem',
                      fontWeight: isSelected ? 600 : 400,
                      padding: '5px 14px',
                      borderRadius: 20,
                      backgroundColor: isSelected ? '#EF60A3' : '#FFF0F5',
                      color: isSelected ? '#fff' : '#EF60A3',
                      border: isSelected ? '1px solid #EF60A3' : '1px solid #F8BBD0',
                      transition: 'all 0.15s',
                    }}
                  >
                    {toAmPm(time)}
                  </button>
                );
              })}
            </div>
          ) : dateStr ? (
            <div>
              <p className="small text-muted mb-1">
                No hay horarios disponibles para esta fecha. Prueba con otro día.
              </p>
              {staffHint && (
                <p
                  className="small d-flex align-items-start gap-1 mb-0"
                  style={{ color: '#B5801A' }}
                >
                  <AlertCircle size={14} className="flex-shrink-0 mt-1" />
                  <span>
                    Alguna de las artistas elegidas no tiene cupo este día. Prueba otra
                    fecha, o vuelve al paso anterior y elige «Sin preferencia» u otra artista.
                  </span>
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* Notes */}
      <Form.Group className="mt-4">
        <Form.Label className="small fw-medium">Notas adicionales (opcional)</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          placeholder="Ej: deseo el servicio con alguna artista en especial..."
          value={notes}
          onChange={e => onNotesChange(e.target.value)}
        />
      </Form.Group>

      {/* Navigation */}
      <div className="d-flex justify-content-between mt-4">
        <Button variant="outline-secondary" onClick={onBack}>Atras</Button>
        <Button
          disabled={!selectedSlot}
          onClick={onContinue}
          style={{
            backgroundColor: '#EF60A3',
            borderColor: '#EF60A3',
            borderRadius: 20,
          }}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
};

export default DateTimeSelector;
